import type { GetProductSingleQuery } from 'services/shopify';
import type { MultiVariantProduct } from 'types/product/MultiVariantProduct';
import type { Product } from 'types/product/Product';
import type { SingleVariantProduct } from 'types/product/SingleVariantProduct';
import type { Subscription } from 'types/product/Subscription';
import type { SubscriptionProduct } from 'types/product/SubscriptionProduct';
import type { Variant } from 'types/product/Variant';
import { convertToNumber } from 'utils/convertToNumber';
import { formatShopifyPrice } from 'utils/formatShopifyPrice';
import type { SellingPlanGroups } from 'utils/getSubProviderSellingPlanIds';
import { getSubProviderSellingPlanIds } from 'utils/getSubProviderSellingPlanIds';

import { mapProductVariant } from './mapProductVariant';

export type ShopifyProductRaw = NonNullable<
  GetProductSingleQuery['productByHandle']
>;

export const mapShopifyProduct = (
  shopifyProduct: ShopifyProductRaw,
): SubscriptionProduct | MultiVariantProduct | SingleVariantProduct => {
  const {
    id,
    productType,
    title,
    availableForSale,
    priceRange,
    variants: shopfifyVariants,
    sellingPlanGroups,
  } = shopifyProduct;

  const subProviderSellingPlanIds = getSubProviderSellingPlanIds(
    sellingPlanGroups as unknown as SellingPlanGroups | null,
  );

  const product: Product = {
    id,
    productType,
    title,
    availableForSale,
    priceRange,
  } as Product;

  const variants: Variant[] = shopfifyVariants.edges.map(({ node }) =>
    mapProductVariant(node),
  );

  const subscriptions: Subscription[] = shopfifyVariants.edges
    .filter(({ node }) => node.sellingPlanAllocations.edges.length > 0)
    .flatMap((v) => {
      const variant = v.node;
      const compareAtPrice = convertToNumber(
        variant.compareAtPrice?.amount as string,
      );
      const amount = convertToNumber(variant.price.amount as string);

      const compareAtPriceAmount =
        compareAtPrice && amount
          ? Math.max(compareAtPrice, amount)
          : amount || null;

      const otpVariant = variants.find((otpV) => otpV.id === variant.id);

      return variant.sellingPlanAllocations.edges
        .filter((edge) =>
          subProviderSellingPlanIds.includes(edge.node.sellingPlan.id),
        )
        .map((s) => {
          const sellingPlanAllocation = s.node;

          return {
            id: variant.id,
            imageUrl:
              variant.image?.url && typeof variant.image?.url === 'string'
                ? variant.image.url
                : undefined,
            title: variant.title,
            maxQuantity: convertToNumber(variant.maxQuantity?.value) || null,
            servings: convertToNumber(variant.servings?.value) || null,
            netContents: variant.netContents?.value || null,
            availableForSale: variant.availableForSale,
            sku: variant.sku || null,
            price: formatShopifyPrice(
              sellingPlanAllocation.priceAdjustments[0].price,
            ),
            compareAtPrice: formatShopifyPrice({
              amount: compareAtPriceAmount,
              currencyCode:
                variant.compareAtPrice?.currencyCode ||
                variant.price.currencyCode,
            }),
            otpPrice: otpVariant?.price ?? formatShopifyPrice(variant.price),

            sellingPlan: {
              id: sellingPlanAllocation.sellingPlan.id,
              title: sellingPlanAllocation.sellingPlan.name,
            },
          };
        });
    });

  // SubscriptionProduct
  if (subscriptions.length) {
    return {
      ...product,
      variants,
      subscriptions,
    };
  }

  // MultiVariantProduct
  if (variants.length > 1) {
    return {
      ...product,
      variants,
    };
  }

  // SingleVariantProduct
  return {
    ...product,
    variants: variants[0] ? [variants[0]] : [],
  };
};
